import type { SchemaMeta } from '@/types';

/**
 * Resolve whether a schema should appear in the sidebar tree, given the
 * user's per-(connection, database) overrides. Mirrors the database-
 * visibility helper.
 *
 * Default behavior:
 *   • schema marked `isDefault` by the driver (Oracle: user's own schema)
 *     → visible
 *   • every other schema → hidden
 *
 * Users opt schemas in via the Database info tab's schema picker. Their
 * choice (stored under `app-store.schemaVisibility`) wins over the
 * default rule here.
 *
 * For drivers that don't set isDefault on any schema (Postgres, MSSQL,
 * MySQL currently), we show every schema — preserving the pre-Oracle
 * behavior. Detecting "no defaults set" requires the caller to pass the
 * full schema list; see `resolveVisibleSchemas` below for the correct
 * entry point.
 */
export function isSchemaVisible(
  schema: SchemaMeta,
  overrides: Record<string, boolean> | undefined,
): boolean {
  const explicit = overrides?.[schema.name];
  if (typeof explicit === 'boolean') return explicit;
  return schema.isDefault === true;
}

/**
 * Filter an array of schemas to the visible subset. If NO schema in the
 * list is marked `isDefault` (Postgres/MySQL/MSSQL), returns all schemas
 * unchanged — this keeps the existing behavior for drivers that don't
 * distinguish a "home" schema. Oracle returns isDefault for the user's
 * own schema, triggering the filter.
 */
export function resolveVisibleSchemas(
  schemas: SchemaMeta[],
  overrides: Record<string, boolean> | undefined,
): SchemaMeta[] {
  const hasDefault = schemas.some((s) => s.isDefault);
  if (!hasDefault) return schemas;
  return schemas.filter((s) => isSchemaVisible(s, overrides));
}
